import { useState } from 'react'
import type { ChartCanvasProps, ReadinessMatrixData } from './chartTypes'
import { TooltipCard } from './TooltipCard'

const cellClass = (status: 'ready' | 'warn' | 'blocked') => `analysisChartMatrixCell analysisChartMatrixCell--${status}`

const cellColor = (status: 'ready' | 'warn' | 'blocked') =>
  status === 'ready' ? 'var(--chart-2)' : status === 'warn' ? 'var(--chart-3)' : 'var(--chart-4)'

type Active = { rowLabel: string; column: string; status: string; color: string; x: number; y: number } | null

const COLUMNS = ['dependencies', 'proof', 'stock', 'budget'] as const

export const ReadinessMatrixChart = ({ data, width, height }: ChartCanvasProps<ReadinessMatrixData>) => {
  const [active, setActive] = useState<Active>(null)
  const headerHeight = 28
  const rowHeight = Math.max(30, Math.min(48, (height - headerHeight - 8) / Math.max(1, data.rows.length)))
  const labelWidth = Math.min(220, width * 0.36)
  const cellWidth = Math.max(52, (width - labelWidth - 12) / COLUMNS.length)

  return (
    <div className="analysisChartCanvas">
      <svg className="analysisChartSvg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Readiness matrix">
        <title>Readiness matrix</title>
        {COLUMNS.map((column, index) => (
          <text
            key={column}
            className="analysisChartAxisText"
            x={labelWidth + index * cellWidth + cellWidth / 2}
            y="20"
            textAnchor="middle"
          >
            {column}
          </text>
        ))}
        {data.rows.map((row, rowIndex) => (
          <g key={row.id} transform={`translate(0 ${headerHeight + rowIndex * rowHeight})`}>
            <text className="analysisChartAxisText" x="0" y={rowHeight / 2 + 4}>{row.label}</text>
            {COLUMNS.map((column, columnIndex) => {
              const cellStatus = row.cells.find((cell) => cell.key === column)?.status ?? 'ready'
              const cellX = labelWidth + columnIndex * cellWidth + 6
              const cellY = 4
              const cellW = cellWidth - 12
              const cellH = rowHeight - 8
              const color = cellColor(cellStatus)
              return (
                <rect
                  key={column}
                  className={cellClass(cellStatus)}
                  x={cellX}
                  y={cellY}
                  width={cellW}
                  height={cellH}
                  rx="7"
                  fill={color}
                  fillOpacity={active?.rowLabel === row.label && active?.column === column ? 0.9 : 0.32}
                  stroke={color}
                  strokeOpacity={0.72}
                  tabIndex={0}
                  role="button"
                  aria-label={`${row.label} · ${column}: ${cellStatus}`}
                  onPointerEnter={() => setActive({ rowLabel: row.label, column, status: cellStatus, color, x: cellX + cellW / 2, y: headerHeight + rowIndex * rowHeight + cellY + cellH / 2 })}
                  onPointerLeave={() => setActive(null)}
                  onFocus={() => setActive({ rowLabel: row.label, column, status: cellStatus, color, x: cellX + cellW / 2, y: headerHeight + rowIndex * rowHeight + cellY + cellH / 2 })}
                  onBlur={() => setActive(null)}
                />
              )
            })}
          </g>
        ))}
      </svg>
      {active && (
        <TooltipCard
          left={active.x}
          top={active.y}
          accent={active.color}
          title={`${active.rowLabel} · ${active.column}`}
          value={active.status}
        />
      )}
    </div>
  )
}
